<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  type User
} from "firebase/auth";
import { Building2, ChevronLeft, ChevronRight, LockKeyhole, LogOut, ShieldCheck, UserPlus } from "lucide-vue-next";
import { firebaseAuth, firebaseConfigured } from "@/services/firebase";
import { apiFetch, publicApiFetch } from "@/services/api";
import { governanceDraft } from "@/content/governance";
import { locale, t } from "@/i18n";
import { authErrorMessageKey, isExistingAccountError, type AuthErrorContext } from "@/domain/auth-errors";
import { automaticSiteId, installationHostname } from "@/domain/installation-guide";
import FirstRunSetup, { type OnboardingResult } from "@/components/auth/FirstRunSetup.vue";
import LanguageSelect from "@/components/layout/LanguageSelect.vue";
import BootSkeleton from "@/components/layout/BootSkeleton.vue";
import Button from "@/volt/Button.vue";
import InputText from "@/volt/InputText.vue";

type OnboardingStatus = {
  tenantId: string | null;
  accepted: boolean;
  requiresDpa: boolean;
  roles: string[];
  organizations?: Array<{ id: string; name: string; roles: string[]; active: boolean }>;
};
type InvitePreview = { organizationName: string; email: string; role: "admin" | "read_only"; expiresAt: string };

const route = useRoute();
const router = useRouter();
const user = ref<User | null>(firebaseAuth?.currentUser ?? null);
const loading = ref(firebaseConfigured);
const inviteLoading = ref(false);
const submitting = ref(false);
const mode = ref<"login" | "register">("login");
const registrationStep = ref(0);
const onboarding = ref<OnboardingStatus>();
const onboardingResult = ref<OnboardingResult>();
const invite = ref<InvitePreview>();
const error = ref("");
const loginForm = reactive({ email: "", password: "" });
const registration = reactive({
  name: "",
  email: "",
  password: "",
  organizationName: "",
  legalName: "",
  controllerAddress: "",
  privacyEmail: "",
  siteName: "",
  siteId: "",
  primaryDomain: "",
  stack: "other",
  acceptTerms: false,
  acceptDpa: false,
  acceptResponsibility: false
});
const governance = reactive({ acceptTerms: false, acceptDpa: false, acceptResponsibility: false });
let unsubscribe: (() => void) | undefined;
const inviteToken = computed(() => {
  // Reading fullPath keeps this fallback reactive across SPA navigation. On a
  // hard deep-link the app can mount before vue-router has populated params,
  // while window.location already contains the authoritative /join URL.
  void route.fullPath;
  if (typeof route.params.token === "string") return route.params.token;
  if (typeof route.query.invite === "string") return route.query.invite;
  if (typeof window === "undefined") return "";
  const match = window.location.pathname.match(/^\/join\/([^/?#]+)/);
  if (!match?.[1]) return "";
  try { return decodeURIComponent(match[1]); }
  catch { return ""; }
});
const legalCopy = computed(() => governanceDraft[locale.value]);
const registrationSteps = computed(() => [t("auth.stepAccount"), t("auth.stepOrganization"), t("auth.stepWebsite"), t("auth.stepConfirm")]);
const inviteEmailMatches = computed(() => Boolean(user.value?.email && invite.value?.email && user.value.email.toLowerCase() === invite.value.email.toLowerCase()));
const registrationStepValid = computed(() => {
  if (registrationStep.value === 0) {
    return registration.name.trim().length >= 2
      && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registration.email)
      && (Boolean(user.value) || registration.password.length >= 8);
  }
  if (registrationStep.value === 1) {
    return registration.organizationName.trim().length >= 2
      && registration.legalName.trim().length >= 2
      && registration.controllerAddress.trim().length >= 5
      && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registration.privacyEmail);
  }
  if (registrationStep.value === 2) {
    return registration.siteName.trim().length >= 2
      && Boolean(installationHostname(registration.primaryDomain));
  }
  return registration.acceptTerms && registration.acceptDpa && registration.acceptResponsibility;
});

function setFlowError(reason: unknown, context: AuthErrorContext) {
  error.value = t(authErrorMessageKey(reason, context));
}

function syncSiteSuggestion() {
  if (!registration.siteName) registration.siteName = registration.organizationName;
  if (!registration.legalName) registration.legalName = registration.organizationName;
  if (!registration.siteId) registration.siteId = automaticSiteId(registration.organizationName);
  if (!registration.privacyEmail) registration.privacyEmail = registration.email;
}
async function loadOnboardingStatus(nextUser: User) {
  const status = await apiFetch<OnboardingStatus>("/onboarding/status");
  onboarding.value = status;
  return status;
}
async function handleAuthUser(next: User | null) {
  user.value = next;
  onboarding.value = undefined;
  if (!inviteToken.value) error.value = "";
  if (next) {
    try {
      const status = await loadOnboardingStatus(next);
      if (!status.tenantId && !invite.value) {
        mode.value = "register";
        registration.email = next.email ?? registration.email;
        loginForm.email = next.email ?? loginForm.email;
      }
    }
    catch { error.value = t("auth.statusFailed"); }
  }
  loading.value = false;
}
async function retryOnboarding() {
  if (!user.value) return;
  loading.value = true;
  await handleAuthUser(user.value);
}
async function login() {
  if (!firebaseAuth) return;
  submitting.value = true;
  error.value = "";
  try {
    await signInWithEmailAndPassword(firebaseAuth, loginForm.email, loginForm.password);
  } catch (reason) {
    setFlowError(reason, "login");
    submitting.value = false;
    return;
  }
  try {
    if (inviteToken.value) await acceptInvite();
  } catch (reason) {
    setFlowError(reason, "invitation");
  }
  finally { submitting.value = false; }
}
async function register() {
  if (!firebaseAuth) return;
  syncSiteSuggestion();
  submitting.value = true;
  error.value = "";
  try {
    let credentialUser: User;
    try {
      credentialUser = firebaseAuth.currentUser ?? (await createUserWithEmailAndPassword(firebaseAuth, registration.email, registration.password)).user;
    } catch (reason) {
      if (inviteToken.value && isExistingAccountError(reason)) {
        mode.value = "login";
        loginForm.email = registration.email;
        error.value = t("auth.accountExists");
        return;
      }
      throw reason;
    }
    if (credentialUser.email?.toLowerCase() !== registration.email.toLowerCase()) throw new Error("ACCOUNT_EMAIL_MISMATCH");
    await updateProfile(credentialUser, { displayName: registration.name });
    if (inviteToken.value) {
      await acceptInvite();
      return;
    }
    const result = await apiFetch<OnboardingResult>("/onboarding/register", {
      method: "POST",
      body: JSON.stringify({
        organizationName: registration.organizationName,
        legalName: registration.legalName,
        controllerAddress: registration.controllerAddress,
        privacyEmail: registration.privacyEmail,
        locale: locale.value,
        siteId: registration.siteId,
        siteName: registration.siteName,
        primaryDomain: installationHostname(registration.primaryDomain),
        environment: "production",
        stack: "other",
        acceptTerms: registration.acceptTerms,
        acceptDataProcessingAgreement: registration.acceptDpa,
        acceptImplementationResponsibility: registration.acceptResponsibility
      })
    });
    onboardingResult.value = result;
    onboarding.value = { tenantId: result.tenant.id, accepted: true, requiresDpa: true, roles: ["admin"] };
    try {
      await credentialUser.getIdToken(true);
    } catch {
      // Registration is already committed. Claims are repaired lazily by the
      // workspace bootstrap; a transient token refresh must not strand the UI.
      void apiFetch("/onboarding/sync-access-claims", { method: "POST" }).catch(() => undefined);
    }
  } catch (reason) {
    setFlowError(reason, inviteToken.value ? "invitation" : "registration");
  } finally { submitting.value = false; }
}
async function acceptGovernance() {
  if (!user.value) return;
  submitting.value = true;
  error.value = "";
  try {
    await apiFetch("/onboarding/accept-governance", {
      method: "POST",
      body: JSON.stringify({
        acceptTerms: governance.acceptTerms,
        acceptDataProcessingAgreement: governance.acceptDpa,
        acceptImplementationResponsibility: governance.acceptResponsibility,
        locale: locale.value
      })
    });
    await user.value.getIdToken(true);
    onboarding.value = { ...onboarding.value!, accepted: true };
  } catch (reason) { setFlowError(reason, "governance"); }
  finally { submitting.value = false; }
}
async function loadInvite() {
  const token = inviteToken.value;
  if (!token) {
    invite.value = undefined;
    inviteLoading.value = false;
    return;
  }
  inviteLoading.value = true;
  error.value = "";
  try {
    const preview = await publicApiFetch<InvitePreview>(`/onboarding/invitations/${encodeURIComponent(token)}`);
    if (inviteToken.value !== token) return;
    invite.value = preview;
    registration.email = invite.value.email;
    loginForm.email = invite.value.email;
    mode.value = "register";
  } catch (reason) {
    if (inviteToken.value === token) setFlowError(reason, "invitation");
  } finally {
    if (inviteToken.value === token) inviteLoading.value = false;
  }
}
async function acceptInvite() {
  if (!firebaseAuth?.currentUser || !inviteToken.value) throw new Error("AUTH_REQUIRED");
  await apiFetch("/onboarding/invitations/accept", {
    method: "POST",
    body: JSON.stringify({ token: inviteToken.value, acceptTerms: registration.acceptTerms || governance.acceptTerms, acceptImplementationResponsibility: registration.acceptResponsibility || governance.acceptResponsibility })
  });
  await firebaseAuth.currentUser.getIdToken(true).catch(() => undefined);
  await loadOnboardingStatus(firebaseAuth.currentUser);
  await router.replace("/visitors");
}
async function confirmInvite() {
  submitting.value = true;
  error.value = "";
  try { await acceptInvite(); }
  catch (reason) { setFlowError(reason, "invitation"); }
  finally { submitting.value = false; }
}
async function signOutForInvite() {
  if (!firebaseAuth) return;
  await signOut(firebaseAuth);
  onboarding.value = undefined;
  mode.value = "register";
  error.value = "";
}
function switchMode(next: "login" | "register") {
  mode.value = next;
  error.value = "";
}
function nextRegistrationStep() {
  if (!registrationStepValid.value) return;
  if (registrationStep.value === 1) syncSiteSuggestion();
  registrationStep.value = Math.min(registrationStep.value + 1, registrationSteps.value.length - 1);
}
async function completeSetup() { onboardingResult.value = undefined; window.location.assign("/visitors"); }

watch(inviteToken, (token) => {
  invite.value = undefined;
  if (token) void loadInvite();
  else inviteLoading.value = false;
}, { immediate: true });
onMounted(() => {
  if (firebaseAuth) unsubscribe = onAuthStateChanged(firebaseAuth, (next) => void handleAuthUser(next));
  else loading.value = false;
});
onUnmounted(() => unsubscribe?.());
defineExpose({ logout: () => firebaseAuth ? signOut(firebaseAuth) : Promise.resolve() });
</script>

<template>
  <LanguageSelect
    v-if="firebaseConfigured && !loading && !inviteLoading && (onboardingResult || !user || onboarding === undefined || !onboarding.tenantId || !onboarding.accepted)"
    class="auth-language-control"
  />
  <BootSkeleton v-if="loading || inviteLoading" :label="t('auth.loading')" />
  <FirstRunSetup v-else-if="onboardingResult" :result="onboardingResult" @complete="completeSetup" />

  <section v-else-if="firebaseConfigured && invite && user && !inviteEmailMatches" class="auth-page">
    <div class="auth-card auth-invite-card">
      <span class="auth-mark"><LogOut /></span>
      <span class="eyebrow">{{ invite.organizationName }}</span>
      <h1>{{ t('auth.wrongAccountTitle') }}</h1>
      <p>{{ t('auth.wrongAccountDescription') }}</p>
      <div class="auth-account-conflict"><span>{{ t('auth.signedInAs') }}</span><strong>{{ user.email }}</strong><small>{{ t('auth.joinAs') }} {{ invite.email }}</small></div>
      <p v-if="error" class="auth-error">{{ error }}</p>
      <Button type="button" variant="primary" :disabled="submitting" @click="signOutForInvite"><LogOut :size="15" /> {{ t('auth.signOutContinue') }}</Button>
    </div>
  </section>

  <form v-else-if="firebaseConfigured && invite && user && inviteEmailMatches" class="auth-page" @submit.prevent="confirmInvite">
    <section class="auth-card auth-invite-card">
      <span class="auth-mark"><ShieldCheck /></span>
      <span class="eyebrow">{{ invite.organizationName }}</span>
      <h1>{{ t('auth.confirmJoinTitle') }}</h1>
      <p>{{ t('auth.confirmJoinDescription') }}</p>
      <div class="auth-account-confirm"><span>{{ t('auth.joinAs') }}</span><strong>{{ invite.email }}</strong><small>{{ invite.role === 'admin' ? 'Admin' : 'Read only' }}</small></div>
      <label class="auth-check"><input v-model="governance.acceptTerms" type="checkbox" required /><span>{{ t('auth.terms') }}</span></label>
      <label class="auth-check"><input v-model="governance.acceptResponsibility" type="checkbox" required /><span>{{ t('auth.responsibility') }}</span></label>
      <details class="setup-disclosure setup-disclosure--nested auth-legal-details"><summary><span><strong>{{ t('auth.legalDraft') }}</strong><small>Vollständige Vertragstexte lesen</small></span><ChevronRight :size="14" aria-hidden="true" /></summary><div class="auth-legal-copy"><pre>{{ legalCopy.terms }}</pre></div></details>
      <p v-if="error" class="auth-error">{{ error }}</p>
      <Button type="submit" variant="primary" :disabled="submitting">{{ submitting ? t('common.loading') : t('auth.join') }}</Button>
    </section>
  </form>

  <section v-else-if="firebaseConfigured && inviteToken && !invite" class="auth-page">
    <div class="auth-card auth-invite-card"><span class="auth-mark"><ShieldCheck /></span><span class="eyebrow">{{ t('auth.joinTitle') }}</span><h1>{{ t('auth.invitationInvalid') }}</h1><p>{{ error }}</p><Button type="button" variant="primary" @click="loadInvite">{{ t('common.retry') }}</Button></div>
  </section>

  <section v-else-if="firebaseConfigured && user && onboarding === undefined" class="auth-page">
    <div class="auth-card">
      <span class="auth-mark"><ShieldCheck /></span>
      <h1>{{ t('auth.statusTitle') }}</h1>
      <p>{{ error || t('auth.statusFailed') }}</p>
      <Button type="button" variant="primary" @click="retryOnboarding">{{ t('common.retry') }}</Button>
    </div>
  </section>

  <form v-else-if="firebaseConfigured && user && onboarding?.tenantId && !onboarding.accepted" class="auth-page" @submit.prevent="acceptGovernance">
    <section class="auth-card auth-governance-card">
      <span class="auth-mark"><ShieldCheck /></span>
      <h1>{{ t('auth.governanceTitle') }}</h1><p>{{ t('auth.governanceDescription') }}</p>
      <label class="auth-check"><input v-model="governance.acceptTerms" type="checkbox" required /><span>{{ t('auth.terms') }}</span></label>
      <label v-if="onboarding.requiresDpa" class="auth-check"><input v-model="governance.acceptDpa" type="checkbox" required /><span>{{ t('auth.dpa') }}</span></label>
      <label class="auth-check"><input v-model="governance.acceptResponsibility" type="checkbox" required /><span>{{ t('auth.responsibility') }}</span></label>
      <details class="setup-disclosure setup-disclosure--nested auth-legal-details"><summary><span><strong>{{ t('auth.legalDraft') }}</strong><small>Vollständige Vertragstexte lesen</small></span><ChevronRight :size="14" aria-hidden="true" /></summary><div class="auth-legal-copy"><pre>{{ legalCopy.terms }}</pre><pre v-if="onboarding.requiresDpa">{{ legalCopy.dpa }}</pre></div></details>
      <p v-if="error" class="auth-error">{{ error }}</p>
      <Button type="submit" variant="primary" :disabled="submitting">{{ submitting ? t('common.loading') : t('auth.acceptContinue') }}</Button>
    </section>
  </form>

  <form v-else-if="firebaseConfigured && invite && !user && mode === 'register'" class="auth-page auth-register-page" @submit.prevent="register">
    <section class="auth-card auth-registration-card auth-invite-card">
      <header><span class="auth-mark"><UserPlus /></span><div><span class="eyebrow">{{ invite.organizationName }}</span><h1>{{ t('auth.joinTitle') }}</h1><p>{{ t('auth.inviteDescription') }}</p></div></header>
      <div class="auth-form-grid">
        <label><span>{{ t('auth.name') }}</span><InputText v-model="registration.name" autocomplete="name" required /></label>
        <label><span>{{ t('auth.email') }}</span><InputText v-model="registration.email" type="email" autocomplete="email" :readonly="Boolean(invite)" required /></label>
        <label class="wide"><span>{{ t('auth.password') }}</span><input v-model="registration.password" class="v-input auth-password" type="password" minlength="8" autocomplete="new-password" required /><small>{{ t('auth.weakPassword') }}</small></label>
      </div>
      <label class="auth-check"><input v-model="registration.acceptTerms" type="checkbox" required /><span>{{ t('auth.terms') }}</span></label>
      <label class="auth-check"><input v-model="registration.acceptResponsibility" type="checkbox" required /><span>{{ t('auth.responsibility') }}</span></label>
      <details class="setup-disclosure setup-disclosure--nested auth-legal-details"><summary><span><strong>{{ t('auth.legalDraft') }}</strong><small>Vollständige Vertragstexte lesen</small></span><ChevronRight :size="14" aria-hidden="true" /></summary><div class="auth-legal-copy"><pre>{{ legalCopy.terms }}</pre></div></details>
      <p v-if="error" class="auth-error">{{ error }}</p>
      <footer><button type="button" class="auth-text-link" @click="switchMode('login')"><Building2 :size="14" /> {{ t('auth.existingAccount') }}</button><Button type="submit" variant="primary" :disabled="submitting">{{ submitting ? t('common.loading') : t('auth.join') }}</Button></footer>
    </section>
  </form>

  <div v-else-if="firebaseConfigured && (!user || !onboarding?.tenantId) && !invite && mode === 'register'" class="auth-page auth-register-page registration-wizard-page">
    <form class="site-wizard registration-wizard setup-flow" aria-labelledby="registration-title" @submit.prevent="registrationStep === 3 ? register() : nextRegistrationStep()">
      <header class="site-wizard__header"><div><h2 id="registration-title">{{ t('auth.register') }}</h2></div><button v-if="!user" type="button" class="auth-text-link" @click="switchMode('login')">{{ t('auth.loginLink') }}</button></header>
      <ol class="site-wizard__steps setup-progress" :style="{ '--setup-steps': registrationSteps.length }"><li v-for="(label, index) in registrationSteps" :key="label" :class="{ active: index === registrationStep, done: index < registrationStep }" :aria-current="index === registrationStep ? 'step' : undefined"><span>{{ index < registrationStep ? '✓' : index + 1 }}</span><small>{{ label }}</small></li></ol>
      <main class="site-wizard__body">
        <section v-if="registrationStep === 0" class="setup-step registration-pane">
          <header class="setup-step__intro"><h3>{{ t('auth.accountStepTitle') }}</h3><p>{{ t('auth.accountStepDescription') }}</p></header>
          <div class="wizard-form"><label><span>{{ t('auth.name') }}</span><InputText v-model="registration.name" autocomplete="name" required /></label><label><span>{{ t('auth.email') }}</span><InputText v-model="registration.email" type="email" autocomplete="email" :readonly="Boolean(user)" required /></label><label v-if="!user" class="wide"><span>{{ t('auth.password') }}</span><input v-model="registration.password" class="v-input auth-password" type="password" minlength="8" autocomplete="new-password" required /><small>Mindestens acht Zeichen.</small></label></div>
        </section>
        <section v-else-if="registrationStep === 1" class="setup-step registration-pane">
          <header class="setup-step__intro"><h3>{{ t('auth.organizationStepTitle') }}</h3><p>{{ t('auth.organizationStepDescription') }}</p></header>
          <div class="wizard-form"><label><span>{{ t('auth.organization') }}</span><InputText v-model="registration.organizationName" required @blur="syncSiteSuggestion" /></label><label><span>{{ t('auth.legalName') }}</span><InputText v-model="registration.legalName" required /><small>Wie im Impressum.</small></label><label class="wide"><span>{{ t('auth.address') }}</span><InputText v-model="registration.controllerAddress" required /></label><label class="wide"><span>{{ t('auth.privacyEmail') }}</span><InputText v-model="registration.privacyEmail" type="email" required /><small>An diese Adresse wenden sich Besucher mit Datenschutzfragen.</small></label></div>
        </section>
        <section v-else-if="registrationStep === 2" class="setup-step registration-pane">
          <header class="setup-step__intro"><h3>{{ t('auth.websiteStepTitle') }}</h3><p>Die Website, auf der Moonshot Firmenbesuche erkennen soll. Weitere Websites kannst du später hinzufügen.</p></header>
          <div class="wizard-form registration-site-form"><label><span>{{ t('auth.siteName') }}</span><InputText v-model="registration.siteName" required /></label><label><span>{{ t('auth.domain') }}</span><InputText v-model="registration.primaryDomain" placeholder="example.com" required /><small>{{ t('auth.domainHelp') }}</small></label></div>
        </section>
        <section v-else class="setup-step registration-pane registration-confirm-pane">
          <header class="setup-step__intro"><h3>{{ t('auth.confirmStepTitle') }}</h3><p>{{ t('auth.confirmStepDescription') }}</p></header>
          <div class="registration-summary"><article><span>{{ t('auth.organization') }}</span><strong>{{ registration.organizationName }}</strong><small>{{ registration.legalName }}</small></article><article><span>{{ t('auth.siteName') }}</span><strong>{{ registration.siteName }}</strong><small>{{ registration.primaryDomain }}</small></article></div>
          <fieldset class="registration-consents"><legend>Zum Abschluss bitte bestätigen</legend>
            <label class="auth-check"><input v-model="registration.acceptTerms" type="checkbox" required /><span>{{ t('auth.terms') }}</span></label>
            <label class="auth-check"><input v-model="registration.acceptDpa" type="checkbox" required /><span>{{ t('auth.dpa') }}</span></label>
            <label class="auth-check"><input v-model="registration.acceptResponsibility" type="checkbox" required /><span>{{ t('auth.responsibility') }}</span></label>
          </fieldset>
          <details class="setup-disclosure setup-disclosure--nested auth-legal-details"><summary><span><strong>{{ t('auth.legalDraft') }}</strong><small>Vollständige Vertragstexte lesen</small></span><ChevronRight :size="14" aria-hidden="true" /></summary><div class="auth-legal-copy"><pre>{{ legalCopy.terms }}</pre><pre>{{ legalCopy.dpa }}</pre></div></details>
        </section>
        <p v-if="error" class="auth-error registration-wizard__error">{{ error }}</p>
      </main>
      <footer class="site-wizard__footer"><Button type="button" variant="ghost" :disabled="registrationStep === 0 || submitting" @click="registrationStep--"><ChevronLeft :size="14" /> {{ t('common.back') }}</Button><Button v-if="registrationStep < 3" type="submit" variant="primary" :disabled="!registrationStepValid || submitting">{{ t('common.next') }} <ChevronRight :size="14" /></Button><Button v-else type="submit" variant="primary" :disabled="!registrationStepValid || submitting">{{ submitting ? t('common.loading') : t('auth.register') }}</Button></footer>
    </form>
  </div>

  <form v-else-if="firebaseConfigured && !user" class="auth-page" @submit.prevent="login">
    <section class="auth-card auth-login-card"><span class="auth-mark"><LockKeyhole /></span><span v-if="invite" class="eyebrow">{{ invite.organizationName }}</span><h1>{{ t('auth.loginTitle') }}</h1><p>{{ invite ? t('auth.inviteDescription') : t('auth.loginDescription') }}</p><label><span>{{ t('auth.email') }}</span><InputText v-model="loginForm.email" type="email" autocomplete="email" :readonly="Boolean(invite)" required /></label><label><span>{{ t('auth.password') }}</span><input v-model="loginForm.password" class="v-input auth-password" type="password" autocomplete="current-password" required /></label><template v-if="invite"><label class="auth-check"><input v-model="governance.acceptTerms" type="checkbox" required /><span>{{ t('auth.terms') }}</span></label><label class="auth-check"><input v-model="governance.acceptResponsibility" type="checkbox" required /><span>{{ t('auth.responsibility') }}</span></label></template><p v-if="error" class="auth-error">{{ error }}</p><Button type="submit" variant="primary" :disabled="submitting">{{ submitting ? t('common.loading') : t('auth.login') }}</Button><button type="button" class="auth-text-link" @click="switchMode('register')"><Building2 :size="14" /> {{ invite ? t('auth.newAccount') : t('auth.registerLink') }}</button></section>
  </form>
  <slot v-else />
</template>
